import DashboardFooter from "./DashboardFooter";
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import PatternBackground from "../../components/PatternBackground";
import { ReactComponent as LifeIcon } from "../../assets/UiElements/life.svg";
import { ReactComponent as PensionIcon } from "../../assets/UiElements/retail.svg";
import { ReactComponent as AddButtonIcon } from "../../assets/UiElements/Buttons.svg";
import { ReactComponent as CorporateIcon } from "../../assets/UiElements/corporate.svg";
// Removed unused Ant Design icons
import LinkAccountModal from "./LinkAccountModal";
import LinkSuccessModal from "./LinkSuccessModal";
import DashboardModal from "../../components/DashboardModal";
import { useProfile } from "../../hooks/useAuth";
import { useAllCorporateAccounts } from "../../hooks/dashboard/useAllCoprateAccount";
import RecommendedForYou from "../../components/RecommendedForYou";
import { usePensionsSummary } from "../../hooks/dashboard/usePensionSummary"; // pensions-summary endpoint
import { usePolicySummary } from "../../hooks/dashboard/usePolicySummary";
import { getUserDetail, notifyUser } from "../../util/util";
//

// Fetch policy summary and log the data (moved inside component)

// Reference card data for All Policies tab (LIFE and PENSION are always shown)
function getBasePolicyCardData(pensionSummaryData, policySummaryData) {
  // LIFE card uses policySummaryData
  const totalPolicies = policySummaryData?.data?.total_policies;
  const totalLifeInvestment = policySummaryData?.data?.total_life_investment;
  const pendingClaims = policySummaryData?.data?.pending_claims;
  const totalClaimAmount = policySummaryData?.data?.total_claim_amount;
  // PENSION card uses pensionSummaryData
  const pensionCount = pensionSummaryData?.data?.total_pensions;

  const formatCurrencyValue = (value) =>
    value ? `GHS ${Number(value).toLocaleString()}` : `GHS 0`;

  const formattedLifeInvestment =
    formatCurrencyValue(totalLifeInvestment) || "GHS 0";
  const formattedPensionPrimary =
    formatCurrencyValue(
      pensionSummaryData?.data?.total_investment ||
        pensionSummaryData?.data?.total_available_balance,
    ) || "GHS 0";
  // For LIFE card, add extraText for 'Cover yourself and your loved ones'
  return [
    {
      label: "Life Insurance",
      accountsText: "You Have",
      accountsCount:
        totalPolicies !== undefined
          ? `${totalPolicies} ${totalPolicies === 1 ? "Policy" : "Policies"}`
          : "0 Policies",
      mainText1: formattedLifeInvestment,
      mainText2: "Available Balance",
      extraText: "",
      icon: "life",
    },
    {
      label: "PENSION",
      accountsText: "You Have",
      accountsCount:
        pensionCount !== undefined
          ? `${pensionCount} Product${pensionCount === 1 ? "" : "s"}`
          : "0 Products",
      mainText1: formattedPensionPrimary,
      mainText2: "Total Contributions",
      icon: "pension",
    },
  ];
}

function DashboardPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Fetch policy summary and log the data
  const { data: policySummaryData } = usePolicySummary();
  useEffect(() => {
    if (policySummaryData) {
      console.log("Fetched policy summary:", policySummaryData);
    }
  }, [policySummaryData]);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showDashboardModal, setShowDashboardModal] = useState(false);
  const [isOutsidePatternBg, setIsOutsidePatternBg] = useState([]);
  // Removed unused linkedCorporate state
  const patternBgRef = useRef(null);
  const cardRefs = useRef([]);

  //
  //

  useEffect(() => {
    const handleScroll = () => {
      if (!patternBgRef.current) return;

      const patternBgBottom =
        patternBgRef.current.getBoundingClientRect().bottom;
      const newVisibility = cardRefs.current.map((card) => {
        if (!card) return false;
        const cardTop = card.getBoundingClientRect().top;
        return cardTop > patternBgBottom;
      });

      setIsOutsidePatternBg(newVisibility);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Initial check
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Fetch all corporate accounts and log the data
  const { data: corporateAccountsData } = useAllCorporateAccounts();
  useEffect(() => {
    if (corporateAccountsData) {
      console.log("Fetched corporate accounts:", corporateAccountsData);
    }
  }, [corporateAccountsData]);

  // Fetch pension summary and log the data
  const { data: pensionSummaryData } = usePensionsSummary();
  useEffect(() => {
    if (pensionSummaryData) {
      console.log("Fetched pension summary:", pensionSummaryData);
    }
  }, [pensionSummaryData]);

  // Show dashboard modal only once after login (tracked via localStorage per user)
  useEffect(() => {
    const user = getUserDetail();
    const userId = user?.member_number || user?.id || 'anonymous';
    const STORAGE_KEY = `dashboard_modal_shown_${userId}`;

    if (!localStorage.getItem(STORAGE_KEY)) {
      setShowDashboardModal(true);
      localStorage.setItem(STORAGE_KEY, 'true');
    }
  }, []);

  // Corporate account card
  let corporateCards = [];
  const corporateList = corporateAccountsData?.data;
  const hideCorporate = process.env.REACT_APP_HIDE_CORPORATE === "true";

  // Debug: log the env var value
  console.log(
    "REACT_APP_COMING_SOON_BUTTONS:",
    process.env.REACT_APP_COMING_SOON_BUTTONS,
  );

  // Check if corporateList exists and has items
  if (!hideCorporate && process.env.REACT_APP_COMING_SOON_BUTTONS !== "true") {
    if (Array.isArray(corporateList) && corporateList.length > 0) {
      // User has corporate accounts - show summary card
      corporateCards = [
        {
          label: "Corporate Account",
          accountsText: "You Have",
          accountsCount: `${corporateList.length} Corporate Account${corporateList.length > 1 ? "s" : ""}`,
          mainText1: "",
          mainText2: "View and manage your corporate accounts",
          icon: "corporate",
          isCorporate: true,
          corpRaw: corporateList,
        },
      ];
    } else {
      // No corporate accounts (empty array or undefined) - show link/add card
      corporateCards = [
        {
          label: "Corporate Account",
          accountsText: "",
          accountsCount: "",
          mainText1: "",
          mainText2: "Link your corporate account",
          icon: "corporate",
          isCorporate: true,
          isLinkCard: true, // Flag to identify this as the link card
        },
      ];
    }
  } else {
    // Coming soon - hide all corporate cards
    corporateCards = [];
  }

  const { data } = useProfile();
  const profile = data?.data || {};
  const getCardStyle = (index, isCorporate = false) => {
    const baseStyle = {
      background: isOutsidePatternBg[index] ? "#1e222d" : "#1e222d",
      color: "#fff",
      borderRadius: 8,
      boxShadow: "0 4px 24px 0 rgba(0,0,0,0.15)",
      minWidth: 280,
      maxWidth: 380,
      minHeight: 260,
      padding: "20px 24px",
      paddingBottom: 20,
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      justifyContent: "flex-start",
      position: "relative",
      cursor: "pointer",
      transition: "all 0.3s ease",
      marginBottom: 24,
      border: isCorporate
        ? isOutsidePatternBg[index]
          ? "2px solid #1e222d"
          : "2px solid #1e222d"
        : "2px solid #1e222d",
    };

    return baseStyle;
  };

  return (
    <div
      className="dashboard-page-root"
      style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}
    >
      <PatternBackground
        ref={patternBgRef}
        fullWidthOnMobile
        style={{ flex: "1 0 auto", display: "flex", flexDirection: "column" }}
        contentStyle={{
          flex: "1 0 auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Only All Policies - no tabs */}
        <div
          className="dashboard-main-content"
          style={{
            width: "100%",
            padding: "0 32px",
            marginTop: 0, // No space between tabs and cards
            position: "relative",
            zIndex: 2,
            flex: "1 0 auto",
            // Ensure content doesn't get overlapped by the full-width footer
            paddingBottom: "220px",
            boxSizing: "border-box",
          }}
        >
          <RecommendedForYou />
          {profile.name && (
            <div
              className="mb-2 dashboard-greeting"
              style={{ fontWeight: 500, color: "#fff" }}
            >
              Hi {profile.name}{" "}
              <span role="img" aria-label="wave">
                👋
              </span>
            </div>
          )}
          <div
            className="mb-4 dashboard-greeting"
            style={{ fontWeight: 500, color: "#fff" }}
          >
            See All Your Products Here.
          </div>
          <div
            className="policy-cards"
            style={{
              display: "flex",
              gap: 24,
              flexWrap: "wrap",
              justifyContent: "flex-start",
            }}
          >
            {[
              ...getBasePolicyCardData(pensionSummaryData, policySummaryData),
              ...corporateCards,
            ].map((data, idx) => {
              const isCorporate =
                data.isCorporate || data.label === "Corporate Account";
              return (
                <div
                  key={idx}
                  ref={(el) => (cardRefs.current[idx] = el)}
                  style={getCardStyle(idx, isCorporate)}
                  onClick={() => {
                    if (isCorporate) {
                      // Check if this is the link card or if user has no corporate accounts
                      if (
                        data.isLinkCard ||
                        !data.corpRaw ||
                        data.corpRaw.length === 0
                      ) {
                        if (
                          process.env.REACT_APP_COMING_SOON_BUTTONS === "true"
                        ) {
                          notifyUser("info", "Coming Soon!", "Info");
                        } else {
                          setShowLinkModal(true);
                        }
                      } else {
                        navigate("/dashboard/corporate");
                      }
                    } else if (data.label === "Life Insurance") {
                      navigate("/dashboard/life");
                    } else if (data.label === "PENSION") {
                      navigate("/dashboard/pension");
                    }
                  }}
                  className="policy-card"
                >
                  <div
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      marginBottom: 6,
                      marginTop: 0,
                      justifyContent: "space-between",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: isCorporate ? "flex-start" : "center",
                        flexDirection: isCorporate ? "column" : "row",
                        justifyContent: "flex-start",
                      }}
                    >
                      {isCorporate ? (
                        <>
                          <span
                            className="card-label"
                            style={{
                              fontWeight: 700,
                              letterSpacing: 1,
                              lineHeight: 1,
                              textAlign: "left",
                            }}
                          >
                            Corporate Account
                          </span>
                        </>
                      ) : (
                        <span
                          className="card-label"
                          style={{
                            fontWeight: 700,
                            letterSpacing: 1,
                            fontSize: 18,
                          }}
                        >
                          {data.label}
                        </span>
                      )}
                    </div>
                    {(!isCorporate || (isCorporate && !data.isLinkCard)) && (
                      <span
                        className="card-arrow"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          color: "#fff",
                          marginLeft: 8,
                        }}
                      >
                        <svg
                          width="28"
                          height="28"
                          viewBox="0 0 28 28"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M5 14H23"
                            stroke="#fff"
                            strokeWidth="2.2"
                            strokeLinecap="round"
                          />
                          <path
                            d="M17 8L23 14L17 20"
                            stroke="#fff"
                            strokeWidth="2.2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </span>
                    )}
                  </div>
                  <div
                    style={{
                      marginBottom: 6,
                      textAlign: "left",
                      width: "100%",
                    }}
                  >
                    {data.accountsText && (
                      <div
                        style={{
                          color: "#fff",
                          fontWeight: 100,
                          fontSize: 16,
                          marginBottom: 2,
                          textAlign: "left",
                        }}
                      >
                        {data.accountsText}
                      </div>
                    )}
                    {data.accountsCount && (
                      <div
                        style={{
                          color: "#ffffffff",
                          fontWeight: 100,
                          fontSize: 19,
                          textAlign: "left",
                        }}
                      >
                        {data.accountsCount}
                      </div>
                    )}
                  </div>
                  <div
                    style={{
                      marginBottom: 6,
                      display: "flex",
                      justifyContent: "flex-start",
                      width: "100%",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center" }}>
                      {data.icon === "life" && (
                        <LifeIcon
                          style={{
                            width: 48,
                            height: 48,
                            display: "block",
                            color: "#fff",
                            fill: "#fff",
                          }}
                        />
                      )}
                      {data.icon === "pension" && (
                        <PensionIcon
                          style={{
                            width: 48,
                            height: 48,
                            display: "block",
                            color: "#fff",
                            fill: "#fff",
                          }}
                        />
                      )}
                      {data.icon === "corporate" && (
                        <>
                          {data.isLinkCard ? (
                            <AddButtonIcon
                              style={{
                                width: 48,
                                height: 48,
                                display: "block",
                              }}
                            />
                          ) : (
                            <CorporateIcon
                              style={{
                                width: 48,
                                height: 48,
                                display: "block",
                              }}
                            />
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  <div
                    style={{
                      color: "#fff",
                      fontWeight:
                        data.label === "PENSION" ||
                        data.label === "Life Insurance"
                          ? 900
                          : 100,
                      fontSize:
                        data.label === "PENSION" ||
                        data.label === "Life Insurance"
                          ? 24
                          : 16,
                      textAlign: "left",
                      width: "100%",
                      marginBottom: 0,
                    }}
                  >
                    {data.mainText1}
                  </div>
                  <div style={{ height: 2 }}></div>
                  <div
                    style={{
                      color: "#fff",
                      fontWeight:
                        (data.label === "Life Insurance" ||
                          data.label === "PENSION") &&
                        data.mainText2
                          ? 700
                          : 100,
                      fontSize: 16,
                      textAlign: "left",
                      width: "100%",
                      marginBottom: 2,
                    }}
                  >
                    {data.mainText2}
                  </div>
                  {/* Add extraText for Life Insurance card below pending claims or above the line */}
                  {data.label === "Life Insurance" && (
                    <div
                      style={{
                        color: "#fff",
                        fontWeight: 100,
                        fontSize: 14,
                        textAlign: "left",
                        width: "100%",
                        marginBottom: 4,
                      }}
                    >
                      {data.extraText}
                    </div>
                  )}
                  <div
                    style={{
                      width: "calc(100% - 48px)",
                      height: 4,
                      borderRadius: 2,
                      background:
                        "linear-gradient(90deg, #006400 0%, #00b050 60%, #90ee90 100%)",
                      position: "absolute",
                      bottom: 4,
                      left: 24,
                    }}
                  ></div>
                </div>
              );
            })}
          </div>
        </div>
      </PatternBackground>
      <DashboardFooter />
      {/* Modals */}
      <LinkAccountModal
        visible={showLinkModal}
        onClose={() => setShowLinkModal(false)}
        onSuccess={(companyValue) => {
          setShowLinkModal(false);
          setShowSuccessModal(true);
          // Refetch corporate accounts to show the newly added account
          queryClient.invalidateQueries(["corporateAccounts"]);
        }}
      />
      <LinkSuccessModal
        visible={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
      />
      <DashboardModal
        open={showDashboardModal}
        onClose={() => setShowDashboardModal(false)}
      />

      <style jsx>{`
        .dashboard-page-root {
          position: relative;
          min-height: 100vh;
          display: flex;
          flex-direction: column;
        }
        .policy-card {
          transition: all 0.3s ease;
        }
        .arrow-hover-view:hover .arrow-view-text,
        .arrow-hover-view:focus .arrow-view-text {
          opacity: 1 !important;
          visibility: visible !important;
        }
        @media (max-width: 768px) {
          .dashboard-page-root {
            padding: 0 16px;
          }
          .dashboard-main-content {
            /* Increase bottom padding on tablet/mobile so content has room above footer */
            padding-bottom: 50px !important;
            padding-left: 12px !important;
            padding-right: 12px !important;
            padding-top: 0 !important;
            margin-top: 0 !important;
            min-height: 0 !important;
            flex: 1 1 auto !important;
          }
          /* Make the PatternBackground occupy full viewport width on small screens */
          .pattern-background {
            width: 100vw !important;
            left: 50% !important;
            right: 50% !important;
            margin-left: -50vw !important;
            margin-right: -50vw !important;
            padding-left: 0 !important;
            padding-right: 0 !important;
          }
          .pattern-background .pattern-content {
            padding-left: 12px !important;
            padding-right: 12px !important;
          }
        }
        @media (max-width: 520px) {
          .dashboard-main-content {
            padding-left: 16px !important;
            padding-right: 16px !important;
            padding-top: 0 !important;
            margin-top: 0 !important;
          }

          .policy-cards {
            display: flex !important;
            gap: 12px !important;
            overflow-x: auto !important;
            -webkit-overflow-scrolling: touch;
            scroll-snap-type: x mandatory;
            padding-bottom: 12px;
            padding-left: 16px;
            padding-right: 16px;
            flex-wrap: nowrap !important;
            /* Hide scrollbar for Chrome, Safari and Opera */
            -ms-overflow-style: none; /* IE and Edge */
            scrollbar-width: none; /* Firefox */
          }
          .policy-cards::-webkit-scrollbar {
            display: none;
          }
          .policy-cards .policy-card {
            flex: 0 0 84% !important;
            min-width: 220px !important;
            max-width: 320px !important;
            margin-bottom: 0 !important;
            scroll-snap-align: start;
          }
        }
      `}</style>
    </div>
  );
}

export default DashboardPage;
